import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useNotifications } from './hooks/useNotifications';

// Auth screens
import Login from './pages/Login';
import Signup from './pages/Signup';

// Practitioner screens
import Dashboard from './pages/practitioner/Dashboard';
import ClientView from './pages/practitioner/ClientView';
import ProtocolTemplates from './pages/practitioner/ProtocolTemplates';
import AddClient from './pages/practitioner/AddClient';
import CustomQuestions from './pages/practitioner/CustomQuestions';
import TeamManagement from './pages/practitioner/TeamManagement';
import SettingsBilling from './pages/practitioner/SettingsBilling';

// Client screens
import TodayView from './pages/client/TodayView';
import DailyWellnessLog from './pages/client/DailyWellnessLog';
import PhotoUpload from './pages/client/PhotoUpload';
import Messages from './pages/client/Messages';
import MyProgress from './pages/client/MyProgress';
import WeeklyCheckIn from './pages/client/WeeklyCheckIn';
import Supplements from './pages/client/Supplements';
import Toolkit from './pages/client/Toolkit';
import ClientSettings from './pages/client/Settings';

// Layout components
import PractitionerLayout from './components/shared/PractitionerLayout';
import ClientLayout from './components/shared/ClientLayout';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="screen container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={role === 'client' ? '/client' : '/practitioner'} replace />;
  }

  return children;
}

export default function App() {
  const { user, role, loading } = useAuth();

  // Initialize push notifications for clients
  useNotifications();

  if (loading) {
    return (
      <div className="screen container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-bark)' }}>Correly</h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: '8px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={user ? <Navigate to={role === 'client' ? '/client' : '/practitioner'} replace /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to={role === 'client' ? '/client' : '/practitioner'} replace /> : <Signup />} />
      <Route path="/signup/:inviteCode" element={<Signup />} />

      {/* Practitioner routes */}
      <Route path="/practitioner" element={
        <ProtectedRoute allowedRoles={['practitioner', 'assistant']}>
          <PractitionerLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="clients/add" element={<AddClient />} />
        <Route path="clients/:clientId" element={<ClientView />} />
        <Route path="clients/:clientId/questions" element={<CustomQuestions />} />
        <Route path="templates" element={<ProtocolTemplates />} />
        <Route path="team" element={<TeamManagement />} />
        <Route path="settings" element={<SettingsBilling />} />
      </Route>

      {/* Client routes */}
      <Route path="/client" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientLayout />
        </ProtectedRoute>
      }>
        <Route index element={<TodayView />} />
        <Route path="supplements" element={<Supplements />} />
        <Route path="log" element={<DailyWellnessLog />} />
        <Route path="checkin" element={<WeeklyCheckIn />} />
        <Route path="photos" element={<PhotoUpload />} />
        <Route path="messages" element={<Messages />} />
        <Route path="progress" element={<MyProgress />} />
        <Route path="toolkit" element={<Toolkit />} />
        <Route path="settings" element={<ClientSettings />} />
      </Route>

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
